import { useState, useEffect, useCallback } from 'react';
import { Session } from '@/types/session';
import { sessionStateManager } from '@/lib/sessionStateManager';
import { supabase } from '@/integrations/supabase/client';

interface UseOrphanedSessionReturn {
  orphanedSession: Session | null;
  isChecking: boolean;
  checkForOrphanedSession: (userId: string) => Promise<void>;
  clearOrphanedSession: () => void;
  restoreOrphanedSession: (session: Session) => void;
}

export function useOrphanedSession(): UseOrphanedSessionReturn {
  const [orphanedSession, setOrphanedSession] = useState<Session | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const checkForOrphanedSession = useCallback(async (userId: string) => {
    if (!userId) return;

    setIsChecking(true);
    
    try {
      // Check if there's a timer state in localStorage
      const timerState = localStorage.getItem('deepfocus_timer_state');
      if (!timerState) {
        setOrphanedSession(null);
        return;
      }

      const parsed = JSON.parse(timerState);
      
      // Validate localStorage has required fields
      if (!parsed.sessionId || !parsed.totalDuration) {
        console.log('Invalid localStorage timer state, clearing');
        localStorage.removeItem('deepfocus_timer_state');
        setOrphanedSession(null);
        return;
      }

      // Check if this session exists in the database with ANY status
      // This includes completed/cancelled sessions that were finished in another tab
      // Use maybeSingle() to avoid 406 error when session doesn't exist
      const { data: sessionData, error } = await supabase
        .from('sessions')
        .select('id, status')
        .eq('id', parsed.sessionId)
        .eq('user_id', userId)
        .maybeSingle();
      
      if (sessionData) {
        // Session exists in database (even if completed/cancelled)
        // This means it was completed/cancelled in another tab - silently clear localStorage
        // Per REQ-16: Everything should happen in the background, user shouldn't see modals
        console.log('✅ Session found in database with status:', sessionData.status, '- silently clearing localStorage (completed in another tab)');
        localStorage.removeItem('deepfocus_timer_state');
        setOrphanedSession(null);
        return;
      }
      
      if (error && error.code !== 'PGRST116') {
        // Database error (not "no rows found")
        console.error('Error checking session in database:', error);
        setOrphanedSession(null);
        return;
      }

      // Session truly doesn't exist in database (PGRST116 = no rows found)
      // This is a real orphaned session - but per REQ-16, we should still handle silently
      // Only show modal if it's a very recent session that might have been lost due to sync issue
      const sessionAge = parsed.startTime ? Date.now() - parsed.startTime : Infinity;
      const isVeryRecent = sessionAge < 5 * 60 * 1000; // Less than 5 minutes old
      
      // For most cases, silently clear old or invalid orphaned sessions
      // Only show modal for very recent sessions that might be recoverable
      if (!isVeryRecent) {
        console.log('🧹 Orphaned session is old or invalid, silently clearing:', parsed.sessionId);
        localStorage.removeItem('deepfocus_timer_state');
        setOrphanedSession(null);
        return;
      }

      // Very recent orphaned session - might be recoverable, show modal
      console.log('⚠️ Very recent orphaned session detected (may need user decision):', parsed.sessionId);
      
      // Create a minimal session object from localStorage for the modal
      const orphanedSession: Session = {
        id: parsed.sessionId,
        user_id: userId,
        goal: parsed.goal || 'Focus Session',
        duration: Math.ceil(parsed.totalDuration / 60), // Convert seconds to minutes
        type: 'solo',
        status: parsed.isPaused ? 'paused' : 'active',
        invite_code: null,
        started_at: parsed.startTime ? new Date(parsed.startTime).toISOString() : null,
        completed_at: null,
        paused_at: parsed.isPaused ? new Date().toISOString() : null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      setOrphanedSession(orphanedSession);
      
    } catch (error) {
      console.error('Error checking for orphaned session:', error);
      setOrphanedSession(null);
    } finally {
      setIsChecking(false);
    }
  }, []);

  const clearOrphanedSession = useCallback(() => {
    console.log('Clearing orphaned session');
    localStorage.removeItem('deepfocus_timer_state');
    setOrphanedSession(null);
  }, []);

  const restoreOrphanedSession = useCallback((session: Session) => {
    console.log('Restoring orphaned session:', session.id);
    // The session will be restored by the TimerContext
    // We just need to clear the orphaned state
    setOrphanedSession(null);
  }, []);

  return {
    orphanedSession,
    isChecking,
    checkForOrphanedSession,
    clearOrphanedSession,
    restoreOrphanedSession,
  };
}

export default useOrphanedSession;
